/**
 * 在途监管平台 — fetch 轻量封装
 * 统一 envelope { code, data, message } 解包；BASE_URL 走环境变量 VITE_API_BASE（默认 /api/v1）。
 */
import type { ApiEnvelope } from '@/types';

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api/v1';
export const TOKEN_KEY = 'yunniuji_transport_token';
export const USER_KEY = 'yunniuji_transport_user';

export class ApiError extends Error {
  code: number;
  status?: number;

  constructor(code: number, message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

function buildQuery(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return '';
  const parts: string[] = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  });
  return parts.join('&');
}

export async function request<T>(
  path: string,
  options: RequestInit = {},
  isFormData = false,
): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = {};
  if (!isFormData) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  const mergedHeaders = { ...headers, ...((options.headers as Record<string, string>) ?? {}) };

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers: mergedHeaders });
  } catch {
    throw new ApiError(0, '网络异常，请检查网络后重试');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await res.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(res.status, `请求失败（HTTP ${res.status}）`, res.status);
  }

  if (!res.ok || body.code !== 0) {
    throw new ApiError(body.code ?? res.status, body.message || `请求失败（HTTP ${res.status}）`, res.status);
  }
  return body.data;
}

export function get<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined | null>,
): Promise<T> {
  const qs = buildQuery(params);
  return request<T>(qs ? `${path}?${qs}` : path);
}

export function post<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) });
}

export function put<T>(path: string, data?: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: data === undefined ? undefined : JSON.stringify(data) });
}
